import { AlertTriangle, ArrowUpRight, Info } from "lucide-react";
import { useState } from "react";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { WalletDebit } from "../types/walletDebits.types";
import { describeDebitSend } from "../utils/walletDebit";
import { WalletDebitAttachments } from "./WalletDebitAttachments";
import { WalletDebitCancel } from "./WalletDebitCancel";
import { WalletDebitReferenceEditor } from "./WalletDebitReferenceEditor";
import { WalletDebitSendForm } from "./WalletDebitSendForm";
import { WalletDebitTimeline } from "./WalletDebitTimeline";

/**
 * What staff do with a debit after reserving it, by status: send it (or cancel it) while pending, correct its
 * reference once sent; its proof and its timeline in every status.
 */
export function WalletDebitManage({
  debit,
  canEdit,
  onChange,
}: {
  debit: WalletDebit;
  /** Allowlisted staff; anyone else only reads. */
  canEdit: boolean;
  onChange: (debit: WalletDebit) => void;
}) {
  const { t, activeLanguage } = useTranslations("walletDebits");
  const send = describeDebitSend(debit, activeLanguage);
  const [uploadFailure, setUploadFailure] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      {debit.status === "RESERVED" && (
        <div className="space-y-3">
          <ColocaAlert
            variant="warning"
            icon={ArrowUpRight}
            heading={t("pending_heading", { amount: send.sent })}
            text={send.rail ? t("pending_text_rail", { rail: send.rail }) : t("pending_text_other")}
          />
          {canEdit && (
            <>
              <WalletDebitSendForm debit={debit} onChange={onChange} onUploadFailed={setUploadFailure} />
              <WalletDebitCancel debit={debit} onChange={onChange} />
            </>
          )}
        </div>
      )}

      {debit.status === "SENT" && (
        <WalletDebitReferenceEditor debit={debit} canEdit={canEdit} onChange={onChange} />
      )}

      {debit.status === "CANCELLED" && (
        <ColocaAlert
          variant="secondary"
          icon={Info}
          text={t("cancelled_text", {
            who: debit.cancelled_by_email ?? "—",
            when: debit.cancelled_at ? formatDateTime(debit.cancelled_at, { locale: activeLanguage }) : "—",
            reason: debit.cancel_reason ?? "—",
          })}
        />
      )}

      {uploadFailure && <ColocaAlert variant="warning" icon={AlertTriangle} text={uploadFailure} />}
      {/* While pending, the send form takes the proof along with the reference. */}
      {(debit.status !== "RESERVED" || debit.attachments.length > 0) && (
        <WalletDebitAttachments
          debit={debit}
          canEdit={canEdit}
          onChange={(next) => {
            setUploadFailure(null);
            onChange(next);
          }}
        />
      )}
      <WalletDebitTimeline debit={debit} />
    </div>
  );
}
